// Step-evidence feature (SaaS): photo/file attachments on manual plan/CM steps,
// stored via S3 presigned uploads. The backend (and the `rust-s3` dep) are gated
// out of the trial, so these hooks are trial-aliased to no-ops and the upload UI
// is hidden. Moved out of `useNetworkPlans` (which ships) so the evidence API
// calls leave the trial bundle entirely.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { config } from '@/config.ts';
import type { StepEvidence } from '@/hooks/useNetworkPlans.ts';

const backendApi = config.backendApi;

interface EvidenceListResponse {
  evidence: StepEvidence[];
}

const listEvidence = async (planId: string, stepId: string): Promise<StepEvidence[]> => {
  const response = await axios.get<EvidenceListResponse>(
    `${backendApi}/api/network-plans/${planId}/steps/${stepId}/evidence`,
    { withCredentials: true },
  );
  return response.data.evidence;
};

interface PresignResponse {
  uploadUrl: string;
  objectKey: string;
  expiresInSeconds: number;
}

/** Presign → PUT the bytes straight to object storage → record the row. */
const uploadEvidence = async (planId: string, stepId: string, file: File): Promise<StepEvidence> => {
  const presign = await axios.post<PresignResponse>(
    `${backendApi}/api/network-plans/${planId}/steps/${stepId}/evidence/presign`,
    { filename: file.name, contentType: file.type || null, sizeBytes: file.size },
    { withCredentials: true },
  );
  // The presigned URL carries its own auth — never attach our session cookie.
  await axios.put(presign.data.uploadUrl, file, {
    withCredentials: false,
    headers: file.type ? { 'Content-Type': file.type } : {},
  });
  const confirm = await axios.post<StepEvidence>(
    `${backendApi}/api/network-plans/${planId}/steps/${stepId}/evidence`,
    {
      objectKey: presign.data.objectKey,
      filename: file.name,
      contentType: file.type || null,
      sizeBytes: file.size,
    },
    { withCredentials: true },
  );
  return confirm.data;
};

const deleteEvidence = async (planId: string, stepId: string, evidenceId: string): Promise<void> => {
  await axios.delete(`${backendApi}/api/network-plans/${planId}/steps/${stepId}/evidence/${evidenceId}`, {
    withCredentials: true,
  });
};

export function useStepEvidence(planId: string | undefined, stepId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['step-evidence', planId, stepId],
    queryFn: () => listEvidence(planId ?? '', stepId ?? ''),
    enabled: enabled && !!planId && !!stepId,
  });
}

export function useUploadEvidence() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, stepId, file }: { planId: string; stepId: string; file: File }) =>
      uploadEvidence(planId, stepId, file),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['step-evidence', variables.planId, variables.stepId] });
    },
  });
}

export function useDeleteEvidence() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ planId, stepId, evidenceId }: { planId: string; stepId: string; evidenceId: string }) =>
      deleteEvidence(planId, stepId, evidenceId),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['step-evidence', variables.planId, variables.stepId] });
    },
  });
}
